import axios, { AxiosInstance } from "axios";

// Define the token response type
interface TokenResponse {
  access_token: string;
  [key: string]: any;
}

// Define API response type with token usage
export interface ApiResponse<T> {
  data: T;
  tokenUsage?: number;
}

class MealPlanApiClient {
  private apiKey: string;
  private accessToken: string | null = null;
  private client: AxiosInstance;
  private tokenUrl: string;
  private baseUrl: string;
  public allowAffiliateLinks = true;

  constructor(apiKey: string) {
    this.apiKey = apiKey;

    // Determine if we're in development environment
    const isDev =
      process.env.NODE_ENV === "development" ||
      window.location.hostname === "localhost" ||
      process.env.REACT_APP_ENV === "development" ||
      window.location.hostname.includes("-dev");

    // Use dev API for development, production API otherwise
    this.baseUrl = isDev
      ? "https://api-dev.passiolife.com/v2"
      : "https://api.passiolife.com/v2";

    if (isDev) {
      this.allowAffiliateLinks = true;
    } else {
      this.allowAffiliateLinks = false;
    }

    this.tokenUrl = `${this.baseUrl}/token-cache/unified/oauth/token/${apiKey}`;

    // Create Axios instance
    this.client = axios.create({
      baseURL: this.baseUrl,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Add request interceptor to log request details
    this.client.interceptors.request.use((config) => {
      // If this is a token request, remove all headers to avoid CORS preflight
      if (
        config.url &&
        config.url.includes("/token-cache/unified/oauth/token/")
      ) {
        config.headers = {}; // Clear all headers
      }

      return config;
    });

    // Add response interceptor to handle common errors
    this.client.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        // Just reject the promise with the error, no console.error
        return Promise.reject(error);
      }
    );
  }

  // Try to fetch token using a hidden iframe to avoid CORS
  private fetchTokenWithIframe(): Promise<string> {
    return new Promise((resolve, reject) => {
      // Create a temporary hidden iframe
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      document.body.appendChild(iframe);

      // Set a timeout to prevent hanging
      const timeoutId = setTimeout(() => {
        document.body.removeChild(iframe);
        reject(new Error("Iframe token fetch timed out"));
      }, 10000);

      // Create a function that will be called by the iframe
      const callbackName = `tokenCallback_${Date.now()}`;

      // Add callback to window
      (window as any)[callbackName] = (data: TokenResponse) => {
        clearTimeout(timeoutId);
        document.body.removeChild(iframe);
        delete (window as any)[callbackName];

        if (data && data.access_token) {
          this.accessToken = data.access_token;
          resolve(data.access_token);
        } else {
          reject(new Error("No token returned from iframe"));
        }
      };

      // Set the iframe source to our API URL with callback parameter
      iframe.src = `${this.baseUrl}/token-cache/unified/oauth/token/${this.apiKey}?callback=${callbackName}`;
    });
  }

  // Fetch token using XMLHttpRequest with minimal headers
  private fetchTokenWithXHR(): Promise<string> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("GET", this.tokenUrl, true);

      // IMPORTANT: Do not set ANY headers to avoid CORS preflight completely
      // Do not set Accept, Content-Type, or any other headers

      // Handle timeouts
      xhr.timeout = 10000;
      xhr.ontimeout = () => {
        reject(new Error("XHR token fetch timed out"));
      };

      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
          if (xhr.status === 200) {
            try {
              const data = JSON.parse(xhr.responseText);
              if (data && data.access_token) {
                this.accessToken = data.access_token;
                resolve(data.access_token);
              } else {
                reject(new Error("No token found in response"));
              }
            } catch (e) {
              reject(new Error(`Failed to parse token response: ${e}`));
            }
          } else {
            reject(new Error(`XHR failed with status: ${xhr.status}`));
          }
        }
      };

      xhr.onerror = () => {
        reject(new Error("XHR network error"));
      };

      // Send the request
      xhr.send();
    });
  }

  // Fetch and refresh the access token
  private async refreshToken(): Promise<string> {
    try {
      try {
        // First try with XMLHttpRequest (most direct browser approach)
        const token = await this.fetchTokenWithXHR();
        return token;
      } catch (xhrError) {
        // Then try with fetch API
        try {
          // Use the most basic fetch request possible to avoid CORS issues
          const response = await fetch(this.tokenUrl, {
            method: "GET",
            // Don't set mode: 'cors'
            // Don't set credentials
            // Don't set ANY headers to avoid CORS preflight
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();

          if (data && data.access_token) {
            this.accessToken = data.access_token;
            return data.access_token;
          }

          throw new Error("No token found in response");
        } catch (fetchError) {
          // Last resort - try iframe approach
          try {
            const token = await this.fetchTokenWithIframe();
            return token;
          } catch (iframeError) {
            throw new Error("Unable to retrieve access token");
          }
        }
      }
    } catch (error) {
      throw new Error(`Failed to get access token: ${error}`);
    }
  }

  // Make sure we have a valid access token
  private async ensureToken(): Promise<void> {
    if (!this.accessToken) {
      try {
        this.accessToken = await this.refreshToken();
      } catch (error) {
        throw new Error(`Authorization failed: ${error}`);
      }
    }
  }

  // Extract token usage from headers
  private extractTokenUsage(headers: any): number | undefined {
    const tokenUsageHeader = headers["x-request-usage"];
    if (tokenUsageHeader) {
      const tokenUsage = parseInt(tokenUsageHeader, 10);
      return isNaN(tokenUsage) ? undefined : tokenUsage;
    }
    return undefined;
  }

  // Parse and prepare the request body
  private prepareRequestBody(content: string): { content: string } {
    try {
      // Parse to make sure it's valid JSON, but we don't modify it
      JSON.parse(content);
      return { content };
    } catch (e) {
      return { content };
    }
  }

  // Generate a meal plan preview
  public async generateMealPlanPreview(
    content: string
  ): Promise<ApiResponse<any>> {
    try {
      // Make sure we have a valid token before making the request
      await this.ensureToken();
      const requestBody = this.prepareRequestBody(content);

      const response = await this.client.post(
        "/products/sdk/tools/generateMealPlanPreview",
        requestBody,
        {
          headers: {
            Authorization: `Bearer ${this.accessToken}`,
          },
        }
      );

      // Extract token usage from headers
      const tokenUsage = this.extractTokenUsage(response.headers);

      return {
        data: response.data,
        tokenUsage,
      };
    } catch (error: any) {
      // Handle token expiration or authentication errors
      if (
        error.response &&
        (error.response.status === 401 || error.response.status === 403)
      ) {
        // Token might be expired, clear it and try again
        this.accessToken = null;
        await this.ensureToken();
        const requestBody = this.prepareRequestBody(content);

        const response = await this.client.post(
          "/products-pr/sdk/tools/generateMealPlanPreview",
          requestBody,
          {
            headers: {
              Authorization: `Bearer ${this.accessToken}`,
            },
          }
        );

        // Extract token usage from headers
        const tokenUsage = this.extractTokenUsage(response.headers);

        return {
          data: response.data,
          tokenUsage,
        };
      }

      // Handle other errors
      throw error;
    }
  }

  // Generate a full meal plan
  public async generateMealPlan(content: string): Promise<ApiResponse<any>> {
    try {
      // Make sure we have a valid token before making the request
      await this.ensureToken();
      const requestBody = this.prepareRequestBody(content);

      const response = await this.client.post(
        "/products/sdk/tools/generateMealPlan",
        requestBody,
        {
          headers: {
            Authorization: `Bearer ${this.accessToken}`,
          },
        }
      );

      // Extract token usage from headers
      const tokenUsage = this.extractTokenUsage(response.headers);

      return {
        data: response.data,
        tokenUsage,
      };
    } catch (error: any) {
      // Handle token expiration or authentication errors
      if (
        error.response &&
        (error.response.status === 401 || error.response.status === 403)
      ) {
        // Token might be expired, clear it and try again
        this.accessToken = null;
        await this.ensureToken();
        const requestBody = this.prepareRequestBody(content);

        const response = await this.client.post(
          "/products-pr/sdk/tools/generateMealPlan",
          requestBody,
          {
            headers: {
              Authorization: `Bearer ${this.accessToken}`,
            },
          }
        );

        // Extract token usage from headers
        const tokenUsage = this.extractTokenUsage(response.headers);

        return {
          data: response.data,
          tokenUsage,
        };
      }

      // Handle other errors
      throw error;
    }
  }

  // Convert meal plan to affiliate link
  public async convertToAffiliateLink(data: any): Promise<ApiResponse<any>> {
    try {
      // Make sure we have a valid token before making the request
      await this.ensureToken();

      const response = await this.client.post(
        "/products/sdk/affiliates/convertMealPlanToAffiliateLink",
        data,
        {
          headers: {
            Authorization: `Bearer ${this.accessToken}`,
          },
        }
      );

      return {
        data: response.data,
      };
    } catch (error: any) {
      // Handle token expiration or authentication errors
      if (
        error.response &&
        (error.response.status === 401 || error.response.status === 403)
      ) {
        // Token might be expired, clear it and try again
        this.accessToken = null;
        await this.ensureToken();

        const response = await this.client.post(
          "/products/sdk/affiliates/convertMealPlanToAffiliateLink",
          data,
          {
            headers: {
              Authorization: `Bearer ${this.accessToken}`,
            },
          }
        );

        return {
          data: response.data,
        };
      }

      // Handle other errors
      throw error;
    }
  }
}

export default MealPlanApiClient;
